import ThresholdManagerWindow from "@/overlay/components/CommonParts/Threshold/ThresholdManagerWindow";
import type { ComponentFactory } from "@/overlay/components/window/Desktop";
import { type DesktopState, defaultDesktopState } from "@/overlay/components/window/DesktopContext";
import { DragItemType } from "@/overlay/components/window/DragItemType";
import { TrackId } from "@/overlay/components/window/TrackId";
import type { WindowIdentityProps } from "@/overlay/components/window/Window";
import { WindowKind, instanceId } from "@/overlay/components/window/WindowKind";
import { Suspense, lazy } from "react";
import { AiChatWindow } from "../AiChatWindow/AiChatWindow";
import { CustomPopup } from "../CustomPopupWindow/popup";
import LoadingWindow from "../LoadingWindow";
import PoiManagerWindow from "../PoiManagerWindow";
import ExploreWindow from "../WhatsNewWindow/ExploreWindow";

// Lazy load window components so the JS is loaded only after the user opens a window first time.
const AccountWindow = lazy(() => import("@/overlay/components/AccountWindow"));
const DatetimeSelectionWindow = lazy(() => import("@/overlay/components/DatetimeSelectionWindow"));
const DeveloperWindow = lazy(() => import("@/overlay/components/DeveloperWindow"));
const LayerStackWindow = lazy(() => import("@/overlay/components/LayerStackWindow"));
const LayoutSettingsWindow = lazy(() => import("@/overlay/components/LayoutSettingsWindow"));
const AboutWindow = lazy(() => import("@/overlay/components/AboutWindow"));
const AddLayerWindow = lazy(() => import("@/overlay/components/AddLayerWindow"));
const AddParameterWindow = lazy(() => import("@/overlay/components/AddParameterWindow/AddParameterWindow"));
const ConfirmationWindow = lazy(() => import("@/overlay/components/ConfirmationWindow"));
const InputConfirmationWindow = lazy(() => import("@/overlay/components/InputConfirmationWindow"));
const DeleteConfirmationWindow = lazy(() => import("@/overlay/components/DeleteConfirmationWindow"));
const PopupPlotWindow = lazy(() => import("@/overlay/components/PopupPlotWindow"));
const FileWindow = lazy(() => import("@/overlay/components//FileWindow"));
const PortfolioManagerWindow = lazy(() => import("@/overlay/components/PortfolioManagerWindow"));
const AddDashboardWindow = lazy(() => import("@/overlay/components/AddDashboardWindow"));
const RenameDashboardWindow = lazy(() => import("@/overlay/components/RenameDashboardWindow"));
const ShortcutsWindow = lazy(() => import("@/overlay/components/ShortcutsWindow"));
const WhatsNewWindow = lazy(() => import("@/overlay/components/WhatsNewWindow"));
const LocationSearchWindow = lazy(() => import("@/overlay/components/LocationSearchWindow/LocationSearchWindow"));
const CalendarControlWindow = lazy(() => import("@/overlay/components/CalendarControlWindow/CalendarControlWindow"));

function withLoadingWindow<T extends WindowKind>(jsx: JSX.Element, props: WindowIdentityProps<T>) {
  return <Suspense fallback={<LoadingWindow {...props} />}>{jsx}</Suspense>;
}

export const windowFactories = [
  {
    instanceId: instanceId(WindowKind.PortfolioManagerWindow),
    component: (key: string, props: WindowIdentityProps<WindowKind.PortfolioManagerWindow>) =>
      withLoadingWindow(<PortfolioManagerWindow key={key} {...props} />, props),
    defaultWindowState: { isVisible: false, position: { trackId: TrackId.Modal } },
    dragItemType: DragItemType.ModalWindow,
    draggable: false,
  },
  // TODO: provide shorthands for the common cxase where id is fixed from the enum. Should be colocated with the window
  {
    instanceId: instanceId(WindowKind.Account),
    component: (key: string, props: WindowIdentityProps<WindowKind.Account>) =>
      withLoadingWindow(<AccountWindow key={key} {...props} />, props),
    defaultWindowState: { position: { trackId: TrackId.Right } },
    dragItemType: DragItemType.NormalWindow,
    draggable: false,
  },
  {
    instanceId: instanceId(WindowKind.AiChatWindow),
    component: (key: string, props: WindowIdentityProps<WindowKind.AiChatWindow>) =>
      withLoadingWindow(<AiChatWindow key={key} {...props} />, props),
    defaultWindowState: { position: { trackId: TrackId.Right } },
    dragItemType: DragItemType.NormalWindow,
    draggable: false,
  },
  {
    instanceId: instanceId(WindowKind.LayoutSettings),
    component: (key: string, props: WindowIdentityProps<WindowKind.LayoutSettings>) =>
      withLoadingWindow(<LayoutSettingsWindow key={key} {...props} />, props),
    defaultWindowState: { position: { trackId: TrackId.Right } },
    dragItemType: DragItemType.NormalWindow,
  },
  {
    instanceId: instanceId(WindowKind.LayerStack),
    component: (key: string, props: WindowIdentityProps<WindowKind.LayerStack>) =>
      withLoadingWindow(<LayerStackWindow key={key} {...props} />, props),
    defaultWindowState: {
      position: { trackId: TrackId.Left },
      isVisible: false,
    },
    dragItemType: DragItemType.NormalWindow,
    floatingBottomOffset: 70,
  },
  {
    instanceId: instanceId(WindowKind.About),
    component: (key: string, props: WindowIdentityProps<WindowKind.About>) =>
      withLoadingWindow(<AboutWindow key={key} {...props} />, props),
    defaultWindowState: {
      isVisible: false,
    },
    dragItemType: DragItemType.NormalWindow,
  },
  {
    instanceId: instanceId(WindowKind.Developer),
    component: (key: string, props: WindowIdentityProps<WindowKind.Developer>) =>
      withLoadingWindow(<DeveloperWindow key={key} {...props} />, props),
    defaultWindowState: { position: { isManaged: false, top: 0, left: 0 } },
    dragItemType: DragItemType.LargeWindow,
  },
  {
    instanceId: instanceId(WindowKind.AddLayer),
    component: (key: string, props: WindowIdentityProps<WindowKind.AddLayer>) =>
      withLoadingWindow(<AddLayerWindow key={key} {...props} />, props),
    defaultWindowState: {
      isVisible: false,
      position: { trackId: TrackId.Modal },
    },
    dragItemType: DragItemType.ModalWindow,
    draggable: false,
  },
  {
    instanceId: instanceId(WindowKind.AddDashboard),
    component: (key: string, props: WindowIdentityProps<WindowKind.AddDashboard>) =>
      withLoadingWindow(<AddDashboardWindow key={key} {...props} />, props),
    defaultWindowState: {
      isVisible: false,
      position: { trackId: TrackId.Modal },
    },
    dragItemType: DragItemType.ModalWindow,
    draggable: false,
  },
  {
    instanceId: instanceId(WindowKind.RenameDashboard),
    component: (key: string, props: WindowIdentityProps<WindowKind.AddDashboard>) =>
      withLoadingWindow(<RenameDashboardWindow key={key} {...props} />, props),
    defaultWindowState: {
      isVisible: false,
      position: { trackId: TrackId.Modal },
    },
    dragItemType: DragItemType.ModalWindow,
    draggable: false,
  },
  {
    instanceId: instanceId(WindowKind.AddParameter),
    component: (key: string, props: WindowIdentityProps<WindowKind.AddParameter>) =>
      withLoadingWindow(<AddParameterWindow key={key} {...props} />, props),
    defaultWindowState: {
      isVisible: false,
      position: { trackId: TrackId.Modal },
    },
    dragItemType: DragItemType.ModalWindow,
    draggable: false,
  },
  {
    instanceId: instanceId(WindowKind.PopupPlot),
    component: (key: string, props: WindowIdentityProps<WindowKind.PopupPlot>) =>
      withLoadingWindow(<PopupPlotWindow key={key} {...props} />, props),
    defaultWindowState: {
      isVisible: false,
      position: { trackId: TrackId.Right },
    },
    dragItemType: DragItemType.NormalWindow,
  },
  {
    instanceId: instanceId(WindowKind.DeleteConfirmation),
    component: (key: string, props: WindowIdentityProps<WindowKind.DeleteConfirmation>) =>
      withLoadingWindow(<DeleteConfirmationWindow key={key} {...props} />, props),
    defaultWindowState: {
      isVisible: false,
      position: { trackId: TrackId.Modal },
    },
    dragItemType: DragItemType.ModalWindow,
    draggable: false,
  },
  {
    instanceId: instanceId(WindowKind.ConfirmationWindow),
    component: (key: string, props: WindowIdentityProps<WindowKind.ConfirmationWindow>) =>
      withLoadingWindow(<ConfirmationWindow key={key} {...props} />, props),
    defaultWindowState: {
      isVisible: false,
      position: { trackId: TrackId.Modal },
    },
    dragItemType: DragItemType.ModalWindow,
    draggable: false,
  },
  {
    instanceId: instanceId(WindowKind.InputConfirmationWindow),
    component: (key: string, props: WindowIdentityProps<WindowKind.InputConfirmationWindow>) =>
      withLoadingWindow(<InputConfirmationWindow key={key} {...props} />, props),
    defaultWindowState: {
      isVisible: false,
      position: { trackId: TrackId.Modal },
    },
    dragItemType: DragItemType.ModalWindow,
    draggable: false,
  },
  {
    instanceId: instanceId(WindowKind.FileWindow),
    component: (key: string, props: WindowIdentityProps<WindowKind.FileWindow>) =>
      withLoadingWindow(<FileWindow key={key} {...props} />, props),
    defaultWindowState: {
      isVisible: false,
      position: { trackId: TrackId.Modal },
    },
    dragItemType: DragItemType.ModalWindow,
    draggable: false,
  },
  {
    instanceId: instanceId(WindowKind.DatetimeSelection),
    component: (key: string, props: WindowIdentityProps<WindowKind.DatetimeSelection>) =>
      withLoadingWindow(<DatetimeSelectionWindow key={key} {...props} />, props),
    defaultWindowState: {
      isVisible: false,
      position: { trackId: TrackId.Bottom },
    },
    dragItemType: DragItemType.NormalWindow,
  },
  {
    instanceId: instanceId(WindowKind.PoiManagerWindow),
    component: (key: string, props: WindowIdentityProps<WindowKind.PoiManagerWindow>) =>
      withLoadingWindow(<PoiManagerWindow key={key} {...props} />, props),
    defaultWindowState: {
      position: { trackId: TrackId.Modal },
      isVisible: false,
    },
    dragItemType: DragItemType.ModalWindow,
    draggable: false,
  },
  {
    instanceId: instanceId(WindowKind.ThresholdManagerWindow),
    component: (key: string, props: WindowIdentityProps<WindowKind.ThresholdManagerWindow>) =>
      withLoadingWindow(<ThresholdManagerWindow key={key} {...props} />, props),
    defaultWindowState: {
      position: { trackId: TrackId.Modal },
      isVisible: false,
    },
    dragItemType: DragItemType.ModalWindow,
    draggable: false,
  },
  {
    instanceId: instanceId(WindowKind.CustomPopup),
    component: (key: string, props: WindowIdentityProps<WindowKind.CustomPopup>) =>
      withLoadingWindow(<CustomPopup key={key} {...props} />, props),
    defaultWindowState: {
      isVisible: false,
      position: { trackId: TrackId.Modal },
    },
    dragItemType: DragItemType.ModalWindow,
    draggable: false,
  },
  {
    instanceId: instanceId(WindowKind.Shortcuts),
    component: (key: string, props: WindowIdentityProps<WindowKind.Shortcuts>) =>
      withLoadingWindow(<ShortcutsWindow key={key} {...props} />, props),
    defaultWindowState: {
      position: { trackId: TrackId.Modal },
      isVisible: false,
    },
    dragItemType: DragItemType.ModalWindow,
    draggable: false,
  },
  {
    instanceId: instanceId(WindowKind.WhatsNewWindow),
    component: (key: string, props: WindowIdentityProps<WindowKind.WhatsNewWindow>) =>
      withLoadingWindow(<WhatsNewWindow key={key} {...props} />, props),
    defaultWindowState: { position: { trackId: TrackId.Right } },
    dragItemType: DragItemType.NormalWindow,
    draggable: false,
  },
  {
    instanceId: instanceId(WindowKind.ExploreWindow),
    component: (key: string, props: WindowIdentityProps<WindowKind.ExploreWindow>) =>
      withLoadingWindow(<ExploreWindow key={key} {...props} />, props),
    defaultWindowState: {
      position: { trackId: TrackId.Modal },
      isVisible: false,
    },
    dragItemType: DragItemType.ModalWindow,
    draggable: false,
  },
  {
    instanceId: instanceId(WindowKind.LocationSearchWindow),
    component: (key: string, props: WindowIdentityProps<WindowKind.LocationSearchWindow>) =>
      withLoadingWindow(<LocationSearchWindow key={key} {...props} />, props),
    defaultWindowState: {
      position: { trackId: TrackId.Modal },
      isVisible: false,
    },
    dragItemType: DragItemType.ModalWindow,
    draggable: false,
  },
  {
    instanceId: instanceId(WindowKind.CalendarControlWindow),
    component: (key: string, props: WindowIdentityProps<WindowKind.CalendarControlWindow>) =>
      withLoadingWindow(<CalendarControlWindow key={key} {...props} />, props),
    defaultWindowState: {
      position: { trackId: TrackId.Modal },
      isVisible: false,
    },
    dragItemType: DragItemType.ModalWindow,
    draggable: false,
  },
];

export const initialDesktopState: DesktopState = defaultDesktopState(windowFactories as ComponentFactory[]);
